/**
 * How the Statistic menu divides its height between the rows and the detail
 * panel under them.
 *
 * **Arithmetic rather than `flex-shrink`, because the race between the two was
 * not predictable.** Driven in a browser with the panel shrinkable and the list
 * `flex-1`: the list took 205px of a 262px menu and squeezed the panel to 28 —
 * one line, hiding half of a detail whose own content measured 55 — although
 * the free space should have gone the other way. Sizing the panel from one
 * number makes both heights a consequence of it instead.
 *
 * It lives in its own module, with no React in it, for the reason
 * `stat-search.ts` states about matching: a rule written inside the JSX is a
 * rule nothing can assert. Four thresholds that decide whether a reviewer can
 * read either half of the menu are exactly the kind of rule that should fail a
 * test rather than a screenshot.
 */

/**
 * The share of the menu kept for the rows.
 *
 * What `field-mapping-usability/01` exists to protect: a search that returns
 * every match is worth nothing in a list three rows tall, which is what a floor
 * of 160 measured out to on a cell two thirds down the Fields table. 184 is the
 * most that can be reserved while still leaving an ordinary panel its measured
 * 76px in the shortest menu `usePlacement` is likely to produce.
 *
 * **It is a budget, not a guarantee, and the degenerate case is the difference.**
 * `usePlacement` floors a menu at 160 whatever the space below the control —
 * deliberately, so a control near the foot of a short window still opens
 * something — and 160 is less than this. There the panel takes `PANEL_FLOOR`
 * and the rows get what is left, which is under the budget. `panelHeight` is
 * written so that this is the only case where that happens, and
 * `tests/stat-menu-layout.test.ts` pins it rather than leaving it to be
 * rediscovered.
 */
export const MENU_FLOOR = 184;

/**
 * The least the panel may have: the Direction line and the facts line.
 *
 * The two it must never lose, because they are the two a reviewer opened it
 * for. Past them it scrolls on its own.
 */
export const PANEL_FLOOR = 52;

/**
 * The most it may have, so no row can swallow a tall menu.
 *
 * **Measured rather than chosen**, against the panel at its real width of 338px
 * on the Fields table. An ordinary row — Direction, the key, the sport and an
 * alias line — needs **76px**, and `games_played`, which carries four aliases,
 * is that row. The worst case is an out-of-sport row, where the two-line
 * warning note sits under the Direction: **130px**.
 *
 * So 136 is a clamp that never actually binds, which is the point. **Nothing
 * clips.** It did when the entry's `description` was shown here — 408
 * characters needed 269px and a ceiling that fitted them would have cost the
 * rows two thirds of the menu — and removing the description removed the
 * problem rather than trading it. `MENU_FLOOR` is 184 so that even a menu
 * squeezed to 262px, measured on a cell two thirds down the table, still hands
 * the panel the 78 an ordinary row asks for.
 */
export const PANEL_CEILING = 136;

/** The detail panel's height inside a menu of `menuHeight`. */
export function panelHeight(menuHeight: number): number {
  return Math.min(PANEL_CEILING, Math.max(PANEL_FLOOR, menuHeight - MENU_FLOOR));
}

/** What the rows are left with, which is what `MENU_FLOOR` is a budget for.
 *  `statusHeight` is the "n of 403 match" line above the list. */
export function rowsHeight(menuHeight: number, statusHeight: number): number {
  return menuHeight - statusHeight - panelHeight(menuHeight);
}
